import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import Scrollspy from 'react-scrollspy';
import { Sticky } from 'react-stickup';
import { Parallax } from 'react-scroll-parallax';
import { StaticImage } from 'gatsby-plugin-image';
import ModalVideo from 'react-modal-video';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Testimonial from '../components/Testimonial';
import CareerBlock from '../components/CareerBlock';
import SuggestedBlogs from '../components/SuggestedBlogs';
import { useDownloadApp } from '../utils/download';
import { useRequestDemo } from '../utils/demo';


const HeroSection = () => {
  const { popupAppClick } = useDownloadApp();

  const { popupDemoClick } = useRequestDemo();

  return (
    <section className="inner-hero-main">
      <div className="container">
        <div
          className="inner-hero-content"
          style={{ maxWidth: '815px', margin: '0 auto' }}>
          <h5>PRODUCT FEATURES</h5>
          <h1>One platform to seamlessly manage your projects and teams.</h1>

          <div className="inner-hero-btns">
            <button type="button" onClick={popupAppClick} className="button">
              Download App
            </button>
            <button
              type="button"
              onClick={popupDemoClick}
              className="button button-border">
              Request Demo
            </button>
          </div>
        </div>
        <div className="product-hero-img">
          <div className="flex-row">
            <div className="col-10 col">
              <img
                src="/img/product-hero.svg"
                className="product-hero"
                alt="product hero"
              />
            </div>
          </div>

          <Parallax className="parallax-product" y={[0, -30]} tagOuter="div">
            <StaticImage
              placeholder="blurred"
              src="../images/product-hero-2.png"
              alt="About Hero Image"
            />
          </Parallax>
        </div>
      </div>
    </section>
  );
};

const Product = ({
  data: {
    blog: { posts = [] },
  },
  ...props
}) => {
  const stickyContainer = React.createRef();

  const anchorClickHandler = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const target = e.target.closest('a');
    const { hash } = new URL(target.href);

    window.scrollTo({
      top: document.querySelector(hash).offsetTop - 100,
      left: 0,
      behavior: 'smooth',
    });
  }, []);
  const [isOpen, setOpen] = useState(false);
  return (
    <Layout {...props}>
      <SEO
        title="How do we help construction companies? | Powerplay features"
        description="Project Planning · Send and track material requests · Generate project DPR/WPR · Site Reporting · Budgeting · Material Requests and Purchase Management"
      />

      <HeroSection />

      <section className="platform-users-main">
        <div className="container">
          <h2 className="heading white-heading text-center">
            Over 150,000 users use our platform.
            <br />
            Here’s how
          </h2>
        </div>
      </section>

      <section ref={stickyContainer}>
        <Sticky container={stickyContainer}>
          <div className="product-scrollspy-main">
            <div className="container">
              <div className="scrollspy-block">
                <Scrollspy
                  offset={-250}
                  items={['section-1', 'section-2', 'section-3']}
                  currentClassName="active">
                  <li>
                    <a href="#section-1" onClick={anchorClickHandler}>
                      Make project plans and assign tasks
                    </a>
                  </li>
                  <li>
                    <a href="#section-2" onClick={anchorClickHandler}>
                      Record material and labour requirements
                    </a>
                  </li>
                  <li>
                    <a href="#section-3" onClick={anchorClickHandler}>
                      Generate reports in a single click
                    </a>
                  </li>
                </Scrollspy>
              </div>
            </div>
          </div>
        </Sticky>

        <div className="container">
          <div className="use-powerplay-single" id="section-1">
            <div className="flex-row align-items-center">
              <div className="col-4 offset-1 col">
                <div className="product-feature-text">
                  <h2 className="heading">
                    Make project plans and assign tasks
                  </h2>
                  <p>
                    Create comprehensive project plans, delegate tasks to
                    engineers, contractors on-site, and managers in the office
                    to get regular updates, and track issues leading to delays.
                  </p>
                </div>
              </div>
              <div className="col-6 offset-1 col">
                <div className="product-feature-img">
                  <img
                    src="/img/make-project-plans.svg"
                    alt="Make project plans and assign tasks"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="use-powerplay-single" id="section-2">
            <div className="flex-row align-items-center mob-row-reverse">
              <div className="col-6 col">
                <div className="product-feature-img">
                  <img
                    src="/img/send-track-materials.svg"
                    alt="Record material and labour requirements"
                  />
                </div>
              </div>
              <div className="col-4 offset-1 col">
                <div className="product-feature-text">
                  <h2 className="heading">
                    Record material and labour requirements
                  </h2>
                  <p>
                    Our simple mobile app makes it easy to register, send, and
                    track material and labour. End to end tracking helps reduce
                    cost overruns.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="use-powerplay-single" id="section-3">
            <div className="flex-row align-items-center">
              <div className="col-4 offset-1 col">
                <div className="product-feature-text">
                  <h2 className="heading">
                    Generate reports in a single click for your customers and
                    managers.
                  </h2>
                  <p>
                    Ensure your customers and managers are always in the loop
                    with daily and weekly progress reports, ensuring open
                    communication and transparency
                  </p>
                </div>
              </div>
              <div className="col-6 offset-1  col">
                <div className="product-feature-img">
                  <img
                    src="/img/daily-progress-report.svg"
                    alt="Generate reports in a single click for your customers and managers."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-lightblue pad100">
        <div className="container">
          <h2
            className="heading text-center"
            style={{ margin: '0 auto 30px', maxWidth: '720px' }}>
            Features that power your construction projects
          </h2>
          <div className="product-features-main">
            <div className="flex-row">
              <div className="col col-3">
                <div className="product-feature-single">
                  <img src="/img/workflow.svg" alt="icon" />
                  <h3>Manage Multiple Projects</h3>
                  <p>
                    Check real-time progress of multiple projects from a single
                    app
                  </p>
                </div>
              </div>
              <div className="col col-3 offset-1">
                <div className="product-feature-single">
                  <img src="/img/management.svg" alt="icon" />
                  <h3>Labour Attendance and Report</h3>
                  <p>
                    Track the presence of workers and manage your on-site
                    workforce needs better
                  </p>
                </div>
              </div>
              <div className="col col-3 offset-1">
                <div className="product-feature-single">
                  <img src="/img/thrive-2.svg" alt="icon" />
                  <h3>Site Reporting</h3>
                  <p>
                    Streamline communication to let your on-site team share
                    real-time updates with pictures
                  </p>
                </div>
              </div>
              <div className="col col-3">
                <div className="product-feature-single">
                  <img src="/img/tasks.svg" alt="icon" />
                  <h3>Tasks and Issues</h3>
                  <p>
                    Assign tasks, highlight issues, and ensure progress is made
                    according to the project timeline
                  </p>
                </div>
              </div>
              <div className="col col-3 offset-1">
                <div className="product-feature-single">
                  <img src="/img/coin.svg" alt="icon" />
                  <h3>Manage Budgeting and BoQ</h3>
                  <p>
                    Oversee the budget and manage billing of quantities with an
                    intuitive dashboard and automated daily use cases
                  </p>
                </div>
              </div>
              <div className="col col-3 offset-1">
                <div className="product-feature-single">
                  <img src="/img/shopping.svg" alt="icon" />
                  <h3>Material Requests and Purchases</h3>
                  <p>
                    Register, send and track material requests and purchase
                    orders from a single platform
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="use-powerplay-main">
        <div className="container">
          <div className="flex-row">
            <div className="col-5 col">
              <div className="use-powerplay">
                <h2 className="heading">
                  How to use powerplay in your organisation
                </h2>
                <p>
                  Powerplay’s mobile collaboration tool is built to let on-site
                  and in-office teams easily collaborate. Make daily schedules,
                  prevent rework, organise inventory, and get updates all on the
                  mobile app.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="side-half-img">
          <ModalVideo
            channel="youtube"
            autoplay
            isOpen={isOpen}
            videoId="K7DL-l9V-dU"
            onClose={() => setOpen(false)}
          />

          <button
            type="button"
            className="play-icon"
            onClick={() => setOpen(true)}>
            <svg
              x="0px"
              y="0px"
              viewBox="0 0 19 22"
              style={{ enableBackground: 'new 0 0 19 22' }}>
              <path
                d="M18.5,10L1.6,0.1C1.3,0,0.9,0,0.5,0.1C0.2,0.3,0,0.7,0,1.1v19.8c0,0.4,0.2,0.8,0.5,1
	C0.7,22,0.9,22,1.1,22c0.2,0,0.4,0,0.5-0.1l16.8-9.9c0.3-0.2,0.5-0.6,0.5-1C19,10.6,18.8,10.2,18.5,10z"
              />
            </svg>
          </button>
        </div>
      </section>

      <Testimonial />
      <CareerBlock />
      <section className="pad100 bg-lightblue">
        <SuggestedBlogs posts={posts} />
      </section>
    </Layout>
  );
};

Product.propTypes = {
  data: PropTypes.shape({
    blog: PropTypes.shape({
      posts: PropTypes.arrayOf(
        PropTypes.shape({
          fields: PropTypes.shape({
            slug: PropTypes.string.isRequired,
          }).isRequired,
          frontmatter: PropTypes.shape({
            title: PropTypes.string.isRequired,
            featuredImage: PropTypes.string.isRequired,
          }).isRequired,
          excerpt: PropTypes.string.isRequired,
          id: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }).isRequired,
  }).isRequired,
};

export default Product;

export const pageQuery = graphql`
  query ProductQuery {
    blog: allMarkdownRemark(limit: 3) {
      posts: nodes {
        frontmatter {
          date(fromNow: true)
          title
          author
          featuredImage
        }
        excerpt
        id
        fields {
          slug
        }
      }
    }
  }
`;
